/**
 * @Author: Klaudie <klaudie@foxprofi.cz>
 */

import { useEffect, useRef, useState, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  ACTIVE_CHECK_INTERVAL,
  IDLE_TIMEOUT,
  ACTIVITY_THROTTLE,
  TOKEN_REFRESH_THRESHOLD,
} from '@/lib/constants'
import {
  getAccessToken,
  getRefreshToken,
  setTokens,
  clearTokens,
  decodeToken,
} from '@/lib/token'
import { refreshToken as refreshTokenApi } from '@/api/core/auth.api'

/**
 * Hook pro automatické obnovování tokenu a sledování aktivity uživatele
 * 
 * Funkce:
 * - Sleduje aktivitu uživatele (pohyb myši, klávesnice, scrollování)
 * - Automaticky obnovuje token každých 5 minut pokud brzy vyprší
 * - Zobrazí idle dialog po 60 minutách nečinnosti
 * - Automaticky odhlásí uživatele pokud nereaguje na idle dialog
 */
export function useTokenRefresh() {
  const navigate = useNavigate()
  const [lastActivityTimestamp, setLastActivityTimestamp] = useState(Date.now())
  const [showIdleDialog, setShowIdleDialog] = useState(false)
  const activityThrottleRef = useRef<number | null>(null)
  const tokenRefreshIntervalRef = useRef<number | null>(null)
  const idleCheckIntervalRef = useRef<number | null>(null)

  // Throttled activity handler
  const handleActivity = useCallback(() => {
    if (activityThrottleRef.current) return

    setLastActivityTimestamp(Date.now())
    setShowIdleDialog(false) // Skrýt dialog pokud uživatel je aktivní

    activityThrottleRef.current = window.setTimeout(() => {
      activityThrottleRef.current = null
    }, ACTIVITY_THROTTLE)
  }, [])

  // Token refresh logic
  const checkAndRefreshToken = useCallback(async () => {
    const accessToken = getAccessToken()
    const refreshTokenValue = getRefreshToken()

    if (!accessToken || !refreshTokenValue) {
      return
    }

    const payload = decodeToken(accessToken)
    if (!payload) {
      return
    }

    const now = Math.floor(Date.now() / 1000)
    const timeUntilExpiry = payload.exp - now

    // Pokud zbývá méně než TOKEN_REFRESH_THRESHOLD sekund, refreshni token
    if (timeUntilExpiry < TOKEN_REFRESH_THRESHOLD) {
      try {
        const response = await refreshTokenApi()
        setTokens(response.accessToken, response.refreshToken)
        console.log('Token automatically refreshed')
      } catch (error) {
        console.error('Failed to refresh token:', error)
        clearTokens()
        navigate('/login')
      }
    }
  }, [navigate])

  // Idle check logic
  const checkIdleTimeout = useCallback(() => {
    const now = Date.now()
    const timeSinceLastActivity = now - lastActivityTimestamp

    // Pokud je uživatel neaktivní déle než IDLE_TIMEOUT, zobraz dialog
    if (timeSinceLastActivity > IDLE_TIMEOUT && !showIdleDialog) {
      setShowIdleDialog(true)
    }
  }, [lastActivityTimestamp, showIdleDialog])

  // Handler pro "Zůstat přihlášen"
  const handleStayLoggedIn = useCallback(() => {
    setLastActivityTimestamp(Date.now())
    setShowIdleDialog(false)
  }, [])

  // Handler pro "Odhlásit se"
  const handleLogout = useCallback(() => {
    clearTokens()
    navigate('/login')
  }, [navigate])

  // Setup activity listeners
  useEffect(() => {
    const events = ['mousemove', 'keydown', 'scroll', 'click']
    
    events.forEach((event) => {
      window.addEventListener(event, handleActivity)
    })

    return () => {
      events.forEach((event) => {
        window.removeEventListener(event, handleActivity)
      })
      if (activityThrottleRef.current) {
        clearTimeout(activityThrottleRef.current)
      }
    }
  }, [handleActivity])

  // Setup token refresh interval
  useEffect(() => {
    // Okamžitá kontrola při mount
    checkAndRefreshToken()

    // Interval pro pravidelnou kontrolu
    tokenRefreshIntervalRef.current = window.setInterval(
      checkAndRefreshToken,
      ACTIVE_CHECK_INTERVAL
    )

    return () => {
      if (tokenRefreshIntervalRef.current) {
        clearInterval(tokenRefreshIntervalRef.current)
      }
    }
  }, [checkAndRefreshToken])

  // Setup idle check interval
  useEffect(() => {
    // Kontrola každou minutu
    idleCheckIntervalRef.current = window.setInterval(checkIdleTimeout, 60000)

    return () => {
      if (idleCheckIntervalRef.current) {
        clearInterval(idleCheckIntervalRef.current)
      }
    }
  }, [checkIdleTimeout])

  return {
    showIdleDialog,
    handleStayLoggedIn,
    handleLogout,
  }
}
